import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';

import { Container, ScreenHeader, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { AccountMenu, PageWrapper } from '../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountScreenContent = () => {
  const { currentBreakpoint } = useResponsive();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderScreenHeader = isNavbarV2Enabled && currentBreakpoint.isXS;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {shouldRenderScreenHeader ? <AccountScreenHeader /> : null}

      <PageWrapper
        style={styles.pageWrapper}
        safeAreaEdges={shouldRenderScreenHeader ? undefined : ['top']}
        safeAreaViewStyle={accountSafeAreaViewStyle}
      >
        <Container style={isNavbarV2Enabled ? undefined : styles.contentContainer}>
          <AccountMenu />
        </Container>
      </PageWrapper>
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const AccountScreenHeader = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleScanPress = useCallback(() => {
    router.navigate('/scan-at-checkout');
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScreenHeader.Container>
      <ScreenHeader.Content>
        <ScreenHeader.Title>
          <FormattedMessage {...messages.accountTitle} />
        </ScreenHeader.Title>

        <ScreenHeader.ScanButton
          title={formatMessage(messages.scanButton)}
          onPress={handleScanPress}
        />
      </ScreenHeader.Content>
    </ScreenHeader.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountTitle: {
    defaultMessage: 'Account',
    description: 'Account | Header | Title',
  },
  scanButton: {
    defaultMessage: 'Scan',
    description: 'Account | Header | Scan button',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  pageWrapper: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentContainer: {
    paddingVertical: theme.spacing['4'],
  },
  safeAreaView: {
    maxHeight: undefined, // overrides default style
  },
});

const accountSafeAreaViewStyle = Platform.select({ native: styles.safeAreaView });
